import { useState, type ReactNode } from 'react';
import { Num, Plural, T, Var, useGT } from 'gt-react';
import {
  CheckCircleIcon,
  QuestionIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import type {
  ValidationEvaluatedViolationDTO,
  ValidationEvaluationCaps,
  ValidationEvaluationTruncation,
  ValidationObservedValue,
  ValidationRunStats,
  ValidationSkippedTable,
  ViolationFullRow,
} from '@erp/rpc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useValidationViolationRowData } from '@/api/rpc/hooks/inventory-management/useValidationRules';
import { cn } from '@/lib/utils';

/**
 * What one evaluation of a validation rule found, rendered for a human: the
 * counts, then a card per violating group showing which divisions carry which
 * value, what the rule proposes, and why.
 *
 * Deliberately free of wizard and rule-page coupling — it takes an evaluation
 * result and nothing else, so the rule builder's preview step and the rule
 * detail page's run view render the same thing. A
 * `PreviewValidationRuleResponse` spreads straight into it.
 */
export interface EvaluationResultViewProps {
  stats: ValidationRunStats;
  /** The violating groups the evaluation reported, in its own order. */
  violations: ValidationEvaluatedViolationDTO[];
  truncated: ValidationEvaluationTruncation;
  /**
   * Division tables the evaluation could not read. Omit when they aren't known
   * (an older run recorded none).
   */
  skippedTables?: ValidationSkippedTable[];
  /** The caps this evaluation ran under. Omit when they aren't known. */
  caps?: ValidationEvaluationCaps | null;
  /** Shown in place of the "everything agrees" copy when there is nothing to show. */
  emptyMessage?: ReactNode;
  className?: string;
}

export function EvaluationResultView({
  stats,
  violations,
  truncated,
  skippedTables,
  caps,
  emptyMessage,
  className,
}: EvaluationResultViewProps) {
  return (
    <div className={cn('space-y-4', className)}>
      <EvaluationStats stats={stats} />

      <SkippedTablesNotice skippedTables={skippedTables ?? []} />

      <TruncationNotice truncated={truncated} caps={caps ?? null} />

      {violations.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-10 text-center text-muted-foreground">
          <CheckCircleIcon className="h-7 w-7 text-green-600" />
          <p className="text-sm">
            {emptyMessage ?? (
              <T context="empty state after evaluating a validation rule and finding no disagreements">
                Every group agreed. Nothing to flag.
              </T>
            )}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {violations.map((violation) => (
            <ValidationViolationCard
              key={violation.violationKey}
              violation={violation}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Stats ───────────────────────────────────────────────────────────────────

function EvaluationStats({ stats }: { stats: ValidationRunStats }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
      <StatTile
        value={stats.recordsEvaluated}
        label={<T context="evaluation stat, ingest rows read">Rows read</T>}
      />
      <StatTile
        value={stats.groupsEvaluated}
        label={
          <T context="evaluation stat, distinct groups the rows fell into">
            Groups
          </T>
        }
      />
      <StatTile
        value={stats.violatingGroups}
        emphasis={stats.violatingGroups > 0 ? 'warning' : undefined}
        label={
          <T context="evaluation stat, groups whose fields disagreed">
            Disagreed
          </T>
        }
      />
      <StatTile
        value={stats.correctionsProposed}
        label={
          <T context="evaluation stat, violations a value could be proposed for">
            Proposed fixes
          </T>
        }
      />
      <StatTile
        value={stats.ambiguousGroups}
        emphasis={stats.ambiguousGroups > 0 ? 'muted' : undefined}
        label={
          <T context="evaluation stat, violations no value could be picked for">
            Ambiguous
          </T>
        }
      />
    </div>
  );
}

function StatTile({
  value,
  label,
  emphasis,
}: {
  value: number;
  label: ReactNode;
  emphasis?: 'warning' | 'muted';
}) {
  return (
    <div className="rounded-lg border bg-muted/20 p-3">
      <div
        className={cn(
          'text-lg font-semibold tabular-nums',
          emphasis === 'warning' && 'text-amber-600',
          emphasis === 'muted' && 'text-muted-foreground',
        )}
      >
        <Num>{value}</Num>
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

// ─── Coverage ────────────────────────────────────────────────────────────────

/**
 * The division tables this evaluation could not read.
 *
 * A rule names a logical schema and is evaluated against every division mapped
 * to it, so a division whose table lacks one of the rule's columns is left out.
 * Left unsaid, that is indistinguishable from a clean result: the rule reads as
 * covering fifteen divisions when it covered twelve.
 */
function SkippedTablesNotice({
  skippedTables,
}: {
  skippedTables: ValidationSkippedTable[];
}) {
  if (skippedTables.length === 0) return null;

  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
      <WarningCircleIcon className="mt-0.5 h-4 w-4 shrink-0" weight="fill" />
      <div className="space-y-1">
        <p>
          <T context="notice that some division tables were left out of a validation evaluation because they lack the rule's columns">
            <Plural
              n={skippedTables.length}
              one={
                <>
                  <Num>{skippedTables.length}</Num> of this schema's division
                  tables was not evaluated: it does not have every column this
                  rule reads.
                </>
              }
              other={
                <>
                  <Num>{skippedTables.length}</Num> of this schema's division
                  tables were not evaluated: they do not have every column this
                  rule reads.
                </>
              }
            />
          </T>
        </p>
        <ul className="space-y-0.5">
          {skippedTables.map((table) => (
            <li
              key={table.dataSourceLinkId}
              className="flex flex-wrap items-center gap-1"
            >
              <Badge variant="outline" className="text-[10px]">
                {table.divisionCode || table.dataSourceName || '—'}
              </Badge>
              <T context="names the columns one division table is missing">
                missing <Var>{table.missingColumns.join(', ')}</Var>
              </T>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── Truncation ──────────────────────────────────────────────────────────────

/**
 * What the evaluation left out. Never inferred from the counts — the evaluator
 * says outright when it stopped early, and the caps say where it stopped.
 */
function TruncationNotice({
  truncated,
  caps,
}: {
  truncated: ValidationEvaluationTruncation;
  caps: ValidationEvaluationCaps | null;
}) {
  if (!truncated.violatingGroups && !truncated.representativeRows) return null;

  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
      <WarningCircleIcon className="mt-0.5 h-4 w-4 shrink-0" weight="fill" />
      <div className="space-y-1">
        {truncated.violatingGroups && (
          <p>
            {caps ? (
              <T context="notice that an evaluation stopped after a capped number of violating groups">
                More groups disagreed than this evaluation reports — it stopped
                at <Num>{caps.maxViolatingGroups}</Num> groups.
              </T>
            ) : (
              <T context="notice that an evaluation stopped early, cap unknown">
                More groups disagreed than this evaluation reports.
              </T>
            )}
          </p>
        )}
        {truncated.representativeRows && (
          <p>
            {caps ? (
              <T context="notice that the rows shown per group are a capped sample">
                The rows shown per group are a sample of up to{' '}
                <Num>{caps.maxRowsPerGroup}</Num>, not every row.
              </T>
            ) : (
              <T context="notice that the rows shown per group are a sample, cap unknown">
                The rows shown per group are a sample, not every row.
              </T>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── One violation ───────────────────────────────────────────────────────────

export interface ValidationViolationCardProps {
  violation: ValidationEvaluatedViolationDTO;
  /**
   * Extra chrome under the disagreement — where the rule detail page hangs a
   * violation's first/last seen, its covering review, and its badges.
   */
  footer?: ReactNode;
}

/**
 * One violating group: which field disagreed, the values each division carries,
 * what the rule proposes, and the rows behind it.
 *
 * An ambiguous violation is drawn as a distinct card rather than as one more
 * row in the pile — nothing will resolve it until a person picks a value, so it
 * says so on its face.
 */
export function ValidationViolationCard({
  violation,
  footer,
}: ValidationViolationCardProps) {
  const gt = useGT();
  const missingLabel = gt('(blank)', {
    context: 'stands in for a missing or empty cell value',
  });
  const needsDecision = violation.outcome.kind === 'ambiguous';

  return (
    <div
      className={cn(
        'space-y-3 rounded-lg border p-3',
        needsDecision &&
          'border-amber-300 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-sm font-medium">
          <T context="heading above one violating group, naming the field that disagreed">
            <Var>{violation.validatedField}</Var> disagrees
          </T>
        </span>
        {needsDecision && (
          <Badge variant="warning">
            <T context="badge on a violation no correction could be proposed for">
              Needs a decision
            </T>
          </Badge>
        )}
        <GroupKeyChips groupKeyValues={violation.groupKeyValues} />
      </div>

      <ObservedValues
        values={violation.observed.values}
        missingLabel={missingLabel}
      />

      <Outcome outcome={violation.outcome} />

      <RepresentativeRows violation={violation} missingLabel={missingLabel} />

      {footer}
    </div>
  );
}

/** The group this violation is about, as `FIELD = value` chips. */
function GroupKeyChips({
  groupKeyValues,
}: {
  groupKeyValues: Record<string, string | null>;
}) {
  return (
    <span className="flex flex-wrap items-center gap-1">
      {Object.entries(groupKeyValues).map(([field, value]) => (
        <Badge key={field} variant="secondary" className="font-mono text-xs">
          {field}
          <span className="opacity-60">=</span>
          {value ?? '—'}
        </Badge>
      ))}
    </span>
  );
}

/** Each distinct value, how many rows carry it, and in which divisions. */
function ObservedValues({
  values,
  missingLabel,
}: {
  values: ValidationObservedValue[];
  missingLabel: string;
}) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <T context="label above the distinct values seen for a validated field">
          Values seen
        </T>
      </p>
      <ul className="space-y-1">
        {values.map((observed, index) => (
          <li
            key={`${observed.value ?? ''}-${index}`}
            className="flex flex-wrap items-center gap-2 text-sm"
          >
            <span
              className={cn(
                'font-mono',
                observed.value === null && 'italic text-muted-foreground',
              )}
            >
              {observed.value ?? missingLabel}
            </span>
            <span className="text-xs text-muted-foreground">
              <T context="how many ingest rows carry one observed value">
                <Num>{observed.rowCount}</Num>{' '}
                <Plural n={observed.rowCount} one="row" other="rows" />
              </T>
            </span>
            <span className="flex flex-wrap gap-1">
              {observed.divisions.map((division) => (
                <Badge
                  key={division.dataSourceId}
                  variant="outline"
                  className="text-[10px]"
                >
                  {division.divisionCode}
                </Badge>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The proposal and the sentence explaining it, or the reason there is none. */
function Outcome({
  outcome,
}: {
  outcome: ValidationEvaluatedViolationDTO['outcome'];
}) {
  if (outcome.kind === 'ambiguous') {
    return (
      <div className="flex items-start gap-2 rounded-md bg-muted/40 p-2 text-xs">
        <QuestionIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div>
          <p className="font-medium">
            <T context="label when a validation rule could not propose a value">
              No value could be picked
            </T>
          </p>
          {/* The evaluator's own sentence — a dynamic English string, like an
              error message, rendered verbatim rather than translated. */}
          <p className="text-muted-foreground">{outcome.warning}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2 rounded-md bg-muted/40 p-2 text-xs">
      <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
      <div>
        <p className="font-medium">
          <T context="label for the value a validation rule proposes for a disagreeing group">
            Proposes <Var>{outcome.value}</Var>
          </T>
        </p>
        {/* The evaluator's own reason sentence, rendered verbatim. */}
        <p className="text-muted-foreground">{outcome.reason}</p>
      </div>
    </div>
  );
}

/**
 * The ingest rows behind the violation. Collapsed, one line per row: the
 * disagreeing cell as stored and as compared. Expanded, the complete ingest
 * record per row — fetched live when the reader asks for it, because a single
 * cell out of context is exactly what makes a violation hard to judge.
 */
function RepresentativeRows({
  violation,
  missingLabel,
}: {
  violation: ValidationEvaluatedViolationDTO;
  missingLabel: string;
}) {
  const [showAllColumns, setShowAllColumns] = useState(false);
  const { rows, rowsTruncated } = violation;
  if (rows.length === 0) return null;

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <T context="label above the sample ingest rows behind a violation">
            Rows
          </T>
        </p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={() => setShowAllColumns((current) => !current)}
        >
          {showAllColumns ? (
            <T context="button collapsing a violation's rows back to just the disagreeing cell">
              Show the disagreeing cell only
            </T>
          ) : (
            <T context="button expanding a violation's rows to every column of the ingest record">
              Show full rows
            </T>
          )}
        </Button>
      </div>
      {showAllColumns ? (
        <FullRowsTable violation={violation} missingLabel={missingLabel} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1 pr-3 font-medium">
                  <T context="column header, the division an ingest row came from">
                    Division
                  </T>
                </th>
                <th className="py-1 pr-3 font-medium">
                  <T context="column header, the location code an ingest row belongs to">
                    Location
                  </T>
                </th>
                <th className="py-1 pr-3 font-medium">
                  <T context="column header, the cell value as the ingest table holds it">
                    Stored value
                  </T>
                </th>
                <th className="py-1 font-medium">
                  <T context="column header, the cell value as the rule compared it">
                    Compared as
                  </T>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={`${row.dataSourceLinkId}-${row.dataId}`}
                  className="border-t"
                >
                  <td className="py-1 pr-3">
                    <Badge variant="outline" className="text-[10px]">
                      {row.divisionCode}
                    </Badge>
                  </td>
                  <td className="py-1 pr-3 font-mono">
                    {/* Details stored before locations were recorded have none. */}
                    {row.location ?? (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="py-1 pr-3 font-mono">
                    {row.rawValue ?? (
                      <span className="italic text-muted-foreground">
                        {missingLabel}
                      </span>
                    )}
                  </td>
                  <td className="py-1 font-mono text-muted-foreground">
                    {row.normalizedValue ?? missingLabel}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rowsTruncated && (
        <p className="text-xs text-muted-foreground">
          <T context="note that the listed rows are a sample of a larger set">
            A sample of the rows in this group.
          </T>
        </p>
      )}
    </div>
  );
}

/** The columns of the fetched rows, in table order, without repeats. */
function orderedColumns(fullRows: ViolationFullRow[]): string[] {
  const columns: string[] = [];
  const seen = new Set<string>();
  for (const row of fullRows) {
    for (const column of row.columns) {
      if (seen.has(column)) continue;
      seen.add(column);
      columns.push(column);
    }
  }
  return columns;
}

/**
 * Every column of each representative row, read live from the ingest tables.
 * The disagreeing column is tinted and the grouping-key columns are emphasized,
 * so the eye lands on what the rule was looking at without losing the rest of
 * the record.
 */
function FullRowsTable({
  violation,
  missingLabel,
}: {
  violation: ValidationEvaluatedViolationDTO;
  missingLabel: string;
}) {
  // The contract caps a request at 50 rows — the evaluator's own per-group
  // sample cap, so in practice this slices nothing.
  const requestRows = violation.rows.slice(0, 50).map((row) => ({
    dataSourceLinkId: row.dataSourceLinkId,
    dataId: row.dataId,
  }));
  const { fullRows, isLoading, error } = useValidationViolationRowData(
    requestRows,
    true,
  );

  if (isLoading) {
    return (
      <p className="py-2 text-xs text-muted-foreground">
        <T context="loading state while a violation's full ingest rows are read">
          Reading the full rows…
        </T>
      </p>
    );
  }
  if (error || fullRows.length === 0) {
    return (
      <p className="py-2 text-xs text-muted-foreground">
        <T context="error state when a violation's full ingest rows cannot be read">
          The full rows could not be read.
        </T>
      </p>
    );
  }

  const columns = orderedColumns(fullRows);
  const validatedColumn = violation.validatedField.toUpperCase();
  const keyColumns = new Set(
    Object.keys(violation.groupKeyValues).map((field) => field.toUpperCase()),
  );

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="sticky left-0 bg-background px-2 py-1 font-medium">
              <T context="column header, the division an ingest row came from">
                Division
              </T>
            </th>
            {columns.map((column) => (
              <th
                key={column}
                className={cn(
                  'px-2 py-1 font-mono font-medium whitespace-nowrap',
                  column === validatedColumn &&
                    'bg-amber-100/70 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
                  keyColumns.has(column) && 'text-foreground',
                )}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fullRows.map((row) => (
            <tr
              key={`${row.dataSourceLinkId}-${row.dataId}`}
              className="border-t"
            >
              <td className="sticky left-0 bg-background px-2 py-1">
                <Badge variant="outline" className="text-[10px]">
                  {row.divisionCode || '—'}
                </Badge>
              </td>
              {row.values === null ? (
                <td
                  colSpan={columns.length}
                  className="px-2 py-1 italic text-muted-foreground"
                >
                  <T context="a violation row whose ingest record can no longer be read">
                    This row can no longer be read.
                  </T>
                </td>
              ) : (
                columns.map((column) => {
                  const value = row.values?.[column] ?? null;
                  return (
                    <td
                      key={column}
                      className={cn(
                        'px-2 py-1 font-mono whitespace-nowrap',
                        column === validatedColumn &&
                          'bg-amber-100/70 dark:bg-amber-950/50',
                        keyColumns.has(column) && 'font-medium',
                      )}
                    >
                      {value ?? (
                        <span className="italic text-muted-foreground">
                          {missingLabel}
                        </span>
                      )}
                    </td>
                  );
                })
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
